// biome-ignore-all lint/suspicious/noArrayIndexKey: notes render newest-first in a stable order and aren't reordered mid-render.
// src/components/admin/ShelterPortalNotesModal.jsx
// ------------------------------------------------
// Draggable modal for a DFV Service's PORTAL notes (internal, e.g. telemarketing —
// NOT synced to Unleashed). Mirrors the comment panel: an "add a note" box at the TOP,
// the notes on file listed BELOW it (newest first). No templates. Adding a note writes
// immediately (an atomic action, like a comment) via the passed onAddNote handler.
import React from "react";
import { ModalBackdrop } from "../form/FormUI.jsx";

function fmtWhen(at) {
  if (!at) return "";
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-AU");
}

export function ShelterPortalNotesModal({ shelterCode, notes, onAddNote, busy, onClose }) {
  const [draft, setDraft] = React.useState("");
  const list = Array.isArray(notes) ? notes : [];

  async function add() {
    const text = draft.trim();
    if (!text || busy) return;
    const ok = await onAddNote(text);
    if (ok !== false) setDraft("");
  }

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      draggable
      dragTitle={`Notes — ${shelterCode || ""}`}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 620,
        maxHeight: "calc(100vh - 60px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }}
    >
      {/* Add-a-note (top) */}
      <div className="p-4 border-b border-zephyr-border">
        <label
          htmlFor="portal-note-draft"
          className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-1 block"
        >
          Add a note
        </label>
        <textarea
          id="portal-note-draft"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a note (e.g. telemarketing call outcome)…"
          className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px]"
          style={{ resize: "vertical", fontFamily: "inherit", minHeight: 80 }}
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={add}
            disabled={busy || draft.trim() === ""}
            className="rounded-[8px] py-[7px] px-4 text-[13px] font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--color-zephyr-teal)", fontFamily: "inherit" }}
          >
            {busy ? "Adding…" : "Add note"}
          </button>
        </div>
      </div>

      {/* Notes on file (bottom, newest first) */}
      <div className="p-4 overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
        <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-2">
          Notes on file
        </div>
        {list.length === 0 ? (
          <div className="text-[13px] text-zephyr-muted italic">No notes yet.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {list.map((n, i) => (
              <div
                key={i}
                className="border border-zephyr-border rounded-[8px] p-3 bg-zephyr-off-white"
              >
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <span className="text-[12px] font-semibold text-zephyr-text">
                    {n.author || "—"}
                  </span>
                  <span className="text-[12px] text-zephyr-muted">{fmtWhen(n.at)}</span>
                </div>
                <div className="text-[13px] text-zephyr-text whitespace-pre-wrap">{n.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ModalBackdrop>
  );
}
